// Semantic Scholar API client — routes through backend proxy to bypass CORS.
// The backend proxy at /api/s2/{path} forwards requests to S2 with the API key.

import apiClient from "@/lib/api-client";
import { toast } from "@/lib/toast";

const S2_PAPER_FIELDS = [
  "paperId",
  "corpusId",
  "externalIds",
  "url",
  "title",
  "abstract",
  "venue",
  "publicationVenue",
  "year",
  "referenceCount",
  "citationCount",
  "influentialCitationCount",
  "isOpenAccess",
  "openAccessPdf",
  "fieldsOfStudy",
  "s2FieldsOfStudy",
  "publicationTypes",
  "publicationDate",
  "journal",
  "authors",
  "citationStyles",
].join(",");

/** What network fetches (citations, references, recommendations) actually
 *  read — `s2PaperToFields` in services/dedup.ts. Every page of a network
 *  costs S2 budget; dropping citationStyles (a BibTeX string per paper),
 *  publicationDate etc. keeps responses small and fast. */
const S2_NETWORK_FIELDS = [
  "paperId",
  "externalIds",
  "url",
  "title",
  "abstract",
  "venue",
  "publicationVenue",
  "year",
  "citationCount",
  "influentialCitationCount",
  "isOpenAccess",
  "openAccessPdf",
  "fieldsOfStudy",
  "journal",
  "authors",
].join(",");

const S2_CITATION_FIELDS = [
  "contexts",
  "intents",
  "isInfluential",
].join(",");

// --- Types ---

export interface S2Author {
  authorId: string | null;
  name: string;
}

export interface S2ExternalIds {
  DOI?: string;
  ArXiv?: string;
  PubMed?: string;
  DBLP?: string;
  MAG?: string;
  CorpusId?: string;
}

export interface S2OpenAccessPdf {
  url: string;
  status: string;
}

export interface S2Journal {
  name?: string;
  volume?: string;
  pages?: string;
}

export interface S2Paper {
  /** null for references S2 could not resolve (title is then raw parser text). */
  paperId: string | null;
  corpusId?: number;
  externalIds?: S2ExternalIds;
  url?: string;
  title: string;
  abstract?: string | null;
  venue?: string;
  publicationVenue?: { name?: string } | null;
  year?: number | null;
  referenceCount?: number;
  citationCount?: number;
  influentialCitationCount?: number;
  isOpenAccess?: boolean;
  openAccessPdf?: S2OpenAccessPdf | null;
  fieldsOfStudy?: string[];
  s2FieldsOfStudy?: { category: string; source: string }[];
  publicationTypes?: string[];
  publicationDate?: string | null;
  journal?: S2Journal | null;
  authors?: S2Author[];
  citationStyles?: { bibtex?: string };
}

export interface S2CitationEdge {
  citingPaper?: S2Paper;
  citedPaper?: S2Paper;
  contexts?: string[];
  intents?: string[];
  isInfluential?: boolean;
}

export interface S2CitationResponse {
  offset: number;
  next?: number;
  /** null when the publisher elided the list — S2 then sends
   *  `{data: null, citingPaperInfo: {…"elided by the publisher"…}}`. */
  data: S2CitationEdge[] | null;
}

export interface S2SearchResponse {
  total: number;
  offset: number;
  next?: number;
  data: S2Paper[];
}

export interface S2MatchResponse {
  paperId: string;
  title: string;
}

// --- Rate Limiter (for proxy — prevents hammering our own backend) ---

export class TokenBucketRateLimiter {
  private tokens: number;
  private lastRefill: number;
  private readonly maxTokens: number;
  private readonly refillRate: number; // tokens per second

  constructor(rps: number) {
    this.maxTokens = rps;
    this.refillRate = rps;
    this.tokens = rps;
    this.lastRefill = Date.now();
  }

  async acquire(): Promise<void> {
    this.refill();
    if (this.tokens >= 1) {
      this.tokens -= 1;
      return;
    }
    const waitMs = ((1 - this.tokens) / this.refillRate) * 1000;
    await new Promise((r) => setTimeout(r, waitMs));
    this.refill();
    this.tokens -= 1;
  }

  private refill() {
    const now = Date.now();
    const elapsed = (now - this.lastRefill) / 1000;
    this.tokens = Math.min(this.maxTokens, this.tokens + elapsed * this.refillRate);
    this.lastRefill = now;
  }
}

// --- Module state ---

// Rate limit client-side — backend proxy handles key rotation and S2 rate limits.
// Set to 5 RPS to allow backend key pool throughput without client-side bottleneck.
// The backend rate limiter is the true gate; this just prevents overwhelming it.
const rateLimiter = new TokenBucketRateLimiter(5);

// --- Internal fetch helper (via backend proxy) ---

/** S2 couldn't answer (rate limited past our retries, timed out, 5xx) — as
 *  opposed to answering "not found" or "nothing here". Thrown only by the
 *  `strict` variants, so callers that must not mistake an outage for an empty
 *  result (the setup preview) can tell them apart. */
export class S2UnavailableError extends Error {
  constructor(path: string) {
    super(`Semantic Scholar unavailable for ${path}`);
    this.name = "S2UnavailableError";
  }
}

/** The backend proxy refused our sign-in (expired or missing token). Thrown by
 *  every variant, lenient ones too: an auth failure must not look like "S2 has
 *  no papers". Shows one "reload to sign in again" toast per burst. */
export class S2AuthError extends Error {
  constructor() {
    super("Your session expired. Reload the page to sign in again.");
    this.name = "S2AuthError";
  }
}

function notifyAuthFailure() {
  toast.error("Your session expired. Reload the page to sign in again.", {
    id: "s2-auth",
    action: { label: "Reload", onClick: () => window.location.reload() },
  });
}

type S2Result<T> = { kind: "ok"; data: T } | { kind: "missing" } | { kind: "failed" };

const MAX_ATTEMPTS = 5;
const BACKOFF_BASE_MS = 2000;
const RETRY_AFTER_CAP_MS = 60_000;

/** Wait before retry: the proxy's Retry-After (from S2) when it's longer than our backoff. */
function retryDelayMs(res: Response, attempt: number): number {
  const backoff = BACKOFF_BASE_MS * Math.pow(2, attempt);
  const header = Number(res.headers.get("retry-after"));
  return Number.isFinite(header) && header > 0 ? Math.min(RETRY_AFTER_CAP_MS, Math.max(backoff, header * 1000)) : backoff;
}

async function s2Request<T>(s2Path: string, signal?: AbortSignal): Promise<S2Result<T>> {
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    await rateLimiter.acquire();

    try {
      // Route through backend proxy: /api/s2/{s2Path}. The backend spaces
      // requests to S2's limit, so a request may queue there for a while.
      const proxyPath = `api/s2${s2Path}`;
      const res = await apiClient.get(proxyPath, {
        signal,
        retry: { limit: 0 },
        throwHttpErrors: false,
        timeout: 30_000,
      });

      if (res.ok) {
        return { kind: "ok", data: (await res.json()) as T };
      }

      if (res.status === 401) {
        notifyAuthFailure();
        throw new S2AuthError();
      }

      // 400 = an id S2 can't parse (e.g. an OpenAlex-only id): an answer, not an outage.
      if (res.status === 400 || res.status === 403 || res.status === 404) {
        return { kind: "missing" };
      }

      if (res.status === 429 || res.status >= 500) {
        const wait = retryDelayMs(res, attempt);
        console.warn(`[S2] ${res.status}, waiting ${wait}ms before retry`);
        if (attempt < MAX_ATTEMPTS - 1) await new Promise((r) => setTimeout(r, wait));
        continue;
      }

      console.warn(`[S2] Unexpected status ${res.status} for ${s2Path}`);
      return { kind: "failed" };
    } catch (err) {
      if (signal?.aborted || err instanceof S2AuthError) throw err;
      // Usually a timeout while queued at the backend: back off rather than
      // re-queue at once (the proxy shares the original's S2 call anyway).
      console.warn(`[S2] Fetch error for ${s2Path}:`, err);
      if (attempt < MAX_ATTEMPTS - 1) await new Promise((r) => setTimeout(r, BACKOFF_BASE_MS * Math.pow(2, attempt)));
    }
  }
  return { kind: "failed" };
}

/** Lenient: null for both "not found" and "S2 couldn't answer". */
async function s2Fetch<T>(s2Path: string, signal?: AbortSignal): Promise<T | null> {
  const res = await s2Request<T>(s2Path, signal);
  return res.kind === "ok" ? res.data : null;
}

/** Strict: null only for "not found"; throws S2UnavailableError when S2 couldn't answer. */
async function s2FetchStrict<T>(s2Path: string, signal?: AbortSignal): Promise<T | null> {
  const res = await s2Request<T>(s2Path, signal);
  if (res.kind === "failed") throw new S2UnavailableError(s2Path);
  return res.kind === "ok" ? res.data : null;
}

// --- Public API ---

export async function fetchCitations(
  paperId: string,
  options: {
    direction: "citations" | "references";
    limit?: number;
    signal?: AbortSignal;
    /** Called after each page of results with the running total of edges. */
    onPage?: (edgesSoFar: number) => void;
    /** Throw S2UnavailableError when a page fails instead of returning what arrived. */
    strict?: boolean;
  },
): Promise<S2CitationEdge[]> {
  const limit = options.limit ?? 100;
  const allEdges: S2CitationEdge[] = [];
  let offset = 0;
  // An S2 failure (null) is not "S2 has no references" — only a real empty
  // answer triggers the OpenAlex fallback below.
  let answered = false;

  while (allEdges.length < limit) {
    const batchSize = Math.min(100, limit - allEdges.length);
    const paperField = options.direction === "citations" ? "citingPaper" : "citedPaper";
    const path = `/graph/v1/paper/${encodeURIComponent(paperId)}/${options.direction}?fields=${S2_CITATION_FIELDS},${paperField}.${S2_NETWORK_FIELDS}&limit=${batchSize}&offset=${offset}`;
    const res = await (options.strict ? s2FetchStrict : s2Fetch)<S2CitationResponse>(path, options.signal);
    if (res) answered = true;
    const page = res?.data ?? [];
    if (page.length === 0) break;
    allEdges.push(...page);
    options.onPage?.(allEdges.length);
    if (res?.next == null) break;
    offset = res.next;
  }

  // S2 withholds many reference lists; the backend fills them from OpenAlex
  // for users in OPENALEX_REFS_RING (empty otherwise). Spec 2026-09-29.
  if (options.direction === "references" && answered && allEdges.length === 0) {
    const fallback = await fetchReferencesFallback(paperId, limit, options.signal, options.strict);
    if (fallback.length > 0) options.onPage?.(fallback.length);
    return fallback;
  }

  return allEdges;
}

async function fetchReferencesFallback(
  paperId: string,
  limit: number,
  signal?: AbortSignal,
  strict = false,
): Promise<S2CitationEdge[]> {
  const path = "api/papers/references-fallback";
  let res: Response;
  try {
    res = await apiClient.get(path, {
      searchParams: { s2_id: paperId, limit: Math.min(limit, 300) },
      signal,
      timeout: 120_000,
      retry: { limit: 0 },
      throwHttpErrors: false,
    });
  } catch (err) {
    if (signal?.aborted) throw err;
    if (strict) throw new S2UnavailableError(path);
    return [];
  }
  // 404 = a backend without the endpoint: no fallback, not an outage.
  if (!res.ok) {
    if (strict && res.status !== 404) throw new S2UnavailableError(path);
    return [];
  }
  const body = (await res.json()) as S2CitationResponse;
  return (body.data ?? []).slice(0, limit);
}

/** A seed's most-cited citers (OpenAlex, resolved to S2 by the backend), as
 *  citation edges. S2's /citations is newest-first only. Never throws except
 *  on abort: [] (outside OPENALEX_REFS_RING, 404 from an old backend, any
 *  failure) means "use S2's list", today's behavior. */
export async function fetchTopCiters(
  paperId: string,
  limit: number,
  signal?: AbortSignal,
  doi?: string | null,
): Promise<S2CitationEdge[]> {
  try {
    const res = await apiClient.get("api/papers/top-citers", {
      // doi: the seed's own, so the backend shares the lane preview's fetch
      // for a seed not imported yet (an older backend ignores it).
      searchParams: { s2_id: paperId, limit: Math.min(limit, 200), ...(doi ? { doi } : {}) },
      signal,
      timeout: 120_000,
      retry: { limit: 0 },
      throwHttpErrors: false,
    });
    if (!res.ok) return [];
    const body = (await res.json()) as S2CitationResponse;
    return (body.data ?? []).slice(0, limit);
  } catch (err) {
    if (signal?.aborted) throw err;
    return [];
  }
}

/** Works most co-cited with a seed that has no references (OpenAlex, resolved
 *  to S2 by the backend), as reference edges. Never throws except on abort:
 *  [] (outside OPENALEX_COCITE_RING, a seed with references, 404 from an old
 *  backend, any failure) means no co-cited direction. */
export async function fetchCocited(
  paperId: string,
  limit: number,
  signal?: AbortSignal,
): Promise<S2CitationEdge[]> {
  try {
    const res = await apiClient.get("api/papers/cocited", {
      searchParams: { s2_id: paperId, limit: Math.min(limit, 100) },
      signal,
      timeout: 120_000,
      retry: { limit: 0 },
      throwHttpErrors: false,
    });
    if (!res.ok) return [];
    const body = (await res.json()) as S2CitationResponse;
    return (body.data ?? []).slice(0, limit);
  } catch (err) {
    if (signal?.aborted) throw err;
    return [];
  }
}

export async function fetchRecommendations(
  paperId: string,
  options?: { limit?: number; signal?: AbortSignal; strict?: boolean },
): Promise<S2Paper[]> {
  const limit = options?.limit ?? 20;
  const path = `/recommendations/v1/papers/forpaper/${encodeURIComponent(paperId)}?fields=${S2_NETWORK_FIELDS}&limit=${limit}`;
  const res = await (options?.strict ? s2FetchStrict : s2Fetch)<{ recommendedPapers: S2Paper[] }>(path, options?.signal);
  return res?.recommendedPapers ?? [];
}

export async function searchPapers(
  query: string,
  options?: { limit?: number; offset?: number; signal?: AbortSignal },
): Promise<S2SearchResponse | null> {
  const limit = options?.limit ?? 50;
  const offset = options?.offset ?? 0;
  const path = `/graph/v1/paper/search?query=${encodeURIComponent(query)}&fields=${S2_PAPER_FIELDS}&limit=${limit}&offset=${offset}`;
  return s2Fetch<S2SearchResponse>(path, options?.signal);
}

/** One paper by DOI (start-from-a-paper). Includes the TLDR for the abstract fallback chain. */
export async function fetchPaperByDoi(doi: string, signal?: AbortSignal): Promise<(S2Paper & { tldr?: { text?: string } | null }) | null> {
  return s2Fetch(`/graph/v1/paper/DOI:${encodeURIComponent(doi)}?fields=${S2_PAPER_FIELDS},tldr`, signal);
}

/**
 * Resolve a paper title to an S2 paper ID.
 * Tries the match endpoint first (exact), then falls back to search (fuzzy).
 */
export async function resolvePaperId(
  title: string,
  signal?: AbortSignal,
  options?: { strict?: boolean },
): Promise<string | null> {
  const get = options?.strict ? s2FetchStrict : s2Fetch;
  // 1. Try match endpoint (fast, exact)
  const matchPath = `/graph/v1/paper/search/match?query=${encodeURIComponent(title)}`;
  const matchRes = await get<S2MatchResponse>(matchPath, signal);
  if (matchRes?.paperId) {
    return matchRes.paperId;
  }

  // 2. Fall back to search (fuzzy) — use first result
  console.warn(`[S2] Match failed for "${title.slice(0, 60)}…", trying search`);
  const searchRes = await get<S2SearchResponse>(
    `/graph/v1/paper/search?query=${encodeURIComponent(title)}&fields=paperId&limit=1&offset=0`, signal,
  );
  if (searchRes?.data?.[0]?.paperId) {
    return searchRes.data[0].paperId;
  }

  console.warn(`[S2] Could not resolve paper ID for "${title.slice(0, 60)}…"`);
  return null;
}
